import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldPath, UseFormReturn } from 'react-hook-form';

import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import { Textarea } from 'components/Textarea';
import { ToggleGroup, ToggleGroupItem } from 'components/ToggleGroup';
import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFLocationSelect } from 'components/input/RHFLocationSelect';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { useFieldAttributes } from 'hooks/useLoadContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import useTMSContext from 'hooks/useTMSContext';
import { useToast } from 'hooks/useToaster';
import { getLocations } from 'lib/api/getLocations';
import {
  LoadDateInput,
  LoadDateTimeInput,
} from 'pages/LoadView/LoadInformation/Components';
import { LoadStopTypes, NormalizedLoad, TMSLocation } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { datetimeFieldOptions } from 'utils/formValidators';
import { normalizeExternalTMSID } from 'utils/loadInfoAndBuilding';
import { cn } from 'utils/shadcn';

import { AILocationHintBanner } from '../components/AiLocationHintBanner';
import { normalizeBusinessHours } from './businessHours';
import { RevenovaTenant } from './constants';
import { getTenantFromString } from './helpers';
import {
  apptRequiredFromTMSLocation,
  buildVirtualLocationFromStop,
  businessHoursFromTMSLocation,
  findLocationByExternalTMSID,
  hasCompleteAddressFields,
  hasNonEmptyString,
  instructionsFromTMSLocation,
  isSelectedLocationResolved,
  mergeLocationsWithStop,
  mergeOrReplaceLocationInList,
} from './stopLocationHelpers';

// Entry mode options for stop forms
export enum StopEntryMode {
  Quick = 'quick',
  Location = 'location',
}

// Only copy these fields from the selected location onto the stop form
const ALLOWED_FIELDS = [
  'name',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zipCode',
  'country',
  'contact',
  'phone',
  'email',
  'refNumber',
  'timezone',
  'readyTime',
  'mustDeliver',
  'apptStartTime',
  'apptEndTime',
  'externalTMSID',
] as const;

interface StopFormProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  locations: Maybe<TMSLocation[]>;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
  isLoadingLocations: boolean;
  setIsLoadingLocations: React.Dispatch<React.SetStateAction<boolean>>;
  stopType: LoadStopTypes;
  isCreateMode?: boolean;
  entryMode?: StopEntryMode;
  setEntryMode?: React.Dispatch<React.SetStateAction<StopEntryMode>>;
  originalSuggestionData?: NormalizedLoad;
}

export function StopForm({
  formMethods,
  locations,
  setLocations,
  isLoadingLocations,
  setIsLoadingLocations,
  stopType,
  isCreateMode = false,
  entryMode: propEntryMode,
  setEntryMode: propSetEntryMode,
  originalSuggestionData,
}: StopFormProps) {
  const { control, watch, setValue, getValues, resetField } = formMethods;
  const { toast } = useToast();
  const { tmsIntegrations } = useServiceFeatures();
  const { tenant } = useTMSContext();
  const fieldAttrs = useFieldAttributes();
  const prefix = stopType;
  const hoursPath = `${prefix}.businessHours` as const;
  const watchedHours = watch(hoursPath);
  const defaultValues = formMethods.formState.defaultValues;
  const hoursHydrationRef = useRef<{
    locationID: string;
    value: string;
    defaults: typeof defaultValues;
  } | null>(null);

  // Normalize suggestions without changing the text while a broker is typing.
  useEffect(() => {
    const state = formMethods.getFieldState(hoursPath);
    if (state.isDirty || state.isTouched || !watchedHours) return;
    const normalized = normalizeBusinessHours(watchedHours);
    if (normalized !== null && normalized !== watchedHours) {
      setValue(hoursPath, normalized, { shouldDirty: false });
    }
  }, [watchedHours, hoursPath, formMethods, setValue]);
  const refNumberAttr = getFieldAttribute(fieldAttrs, `${prefix}.refNumber`);
  const DEFAULT_CONTACT_PLACEHOLDER = 'Not Specified';

  const isCoolerTenant = tenant
    ? getTenantFromString(tenant) === RevenovaTenant.Cooler
    : false;

  // Load-view hydrate must update defaultValues too — fieldState.isDirty compares
  // against defaults, so setValue(..., { shouldDirty: false }) still yellow-highlights.
  const applyHydratedValue = useCallback(
    (path: FieldPath<NormalizedLoad>, value: unknown) => {
      if (isCreateMode) {
        setValue(path, value as never, { shouldDirty: true });
        return;
      }
      resetField(path, { defaultValue: value as never });
    },
    [isCreateMode, setValue, resetField]
  );

  const [localEntryMode, setLocalEntryMode] = useState<StopEntryMode>(
    () => StopEntryMode.Location
  );

  const entryMode = propEntryMode ?? localEntryMode;
  const setEntryMode = propSetEntryMode ?? setLocalEntryMode;
  const prevEntryModeRef = useRef(entryMode);
  const prevDefaultValuesRef = useRef(defaultValues);
  const isEntryModeChanging = prevEntryModeRef.current !== entryMode;
  // Keep the Quick Entry draft separate from the selected location's name.
  const quickEntryNameRef = useRef<string | undefined>(undefined);
  // Store location entry field values when switching to quick entry
  const [locationFieldValues, setLocationFieldValues] = useState<{
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    externalTMSID?: string;
    timezone?: string;
  }>({});

  const handleRefreshLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await getLocations(tmsIntegrations?.[0]?.id);
      if (response.isOk()) {
        const currentStop = formMethods.getValues(prefix);
        setLocations(
          mergeLocationsWithStop(response.value.locationList, currentStop) ??
            response.value.locationList
        );
        toast({
          description: 'Successfully refreshed location list.',
          variant: 'success',
        });
      } else {
        toast({
          description: response.error.message || 'Failed to refresh locations',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to refresh locations',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const watchedLocationID = watch(`${prefix}.externalTMSID`);
  const watchedLocObj = watch(prefix);
  const watchedTimezone = watch(`${prefix}.timezone`);

  // Track the previous location ID to detect when selection just changed.
  // Used to skip the field-mismatch check during the hydration window.
  const prevLocationIDRef = useRef<string | null>(null);
  // Separate from prevLocationIDRef so hydrate can detect set→empty without
  // depending on the address-mismatch effect's update order.
  const prevHydratedLocationIDRef = useRef<string | null>(null);

  const selectedLocation = useMemo(
    () => findLocationByExternalTMSID(locations, watchedLocationID),
    [locations, watchedLocationID]
  );

  const locationFieldsReadOnly =
    entryMode === StopEntryMode.Location &&
    isSelectedLocationResolved(locations, watchedLocationID) &&
    !!selectedLocation &&
    hasCompleteAddressFields(selectedLocation);

  const hasValidAISuggestion = hasValidAISuggestedLocation(
    stopType,
    getValues(),
    originalSuggestionData
  );
  const shouldShowAIHint =
    isCreateMode &&
    entryMode === StopEntryMode.Location &&
    !normalizeExternalTMSID(watchedLocationID) &&
    hasValidAISuggestion;

  // Stop timezone for labels / datetime interpretation when a location is set.
  // Omit when unset — shared inputs hide the TZ suffix and use UTC internally.
  const getDefaultTimezone = (): string | undefined => {
    if (watchedTimezone) {
      return watchedTimezone;
    }
    return undefined;
  };

  // Keep the hydrated locations list in sync with the form when the TMS row is
  // missing or search/refresh temporarily shrinks the list.
  useEffect(() => {
    // The first render after a mode switch still contains the Quick Entry
    // draft. Wait for the restore effect before syncing the location picker.
    if (entryMode !== StopEntryMode.Location || isEntryModeChanging) {
      return;
    }
    if (!watchedLocObj?.name?.trim()) {
      return;
    }

    const normalizedId = normalizeExternalTMSID(watchedLocationID);
    const virtualLocation = buildVirtualLocationFromStop(
      normalizedId,
      watchedLocObj
    );
    if (!virtualLocation) {
      return;
    }

    setLocations((prev) => mergeOrReplaceLocationInList(prev, virtualLocation));
  }, [
    entryMode,
    isEntryModeChanging,
    watchedLocObj,
    watchedLocationID,
    setLocations,
  ]);

  useEffect(() => {
    const normalizedId = normalizeExternalTMSID(watchedLocationID);
    const prevHydratedId = prevHydratedLocationIDRef.current;
    prevHydratedLocationIDRef.current = normalizedId || null;

    if (!normalizedId || !selectedLocation) {
      // Remember the pre-hydration value so delayed location data cannot erase
      // an edit made while the catalog was loading.
      if (!normalizedId) {
        hoursHydrationRef.current = null;
      } else if (
        hoursHydrationRef.current?.locationID !== normalizedId ||
        hoursHydrationRef.current.defaults !== defaultValues
      ) {
        hoursHydrationRef.current = {
          locationID: normalizedId,
          value: formMethods.getValues(hoursPath) || '',
          defaults: defaultValues,
        };
      }
      // Location-owned flag: clear only on set→empty (cleared picker / Quick
      // Entry). Skip first paint and update loads that have apptRequired but
      // no TMS location — writing false would dirty null create defaults or
      // wipe a load value the disabled checkbox cannot restore.
      if (!normalizedId && prevHydratedId) {
        applyHydratedValue(
          `${prefix}.apptRequired` as FieldPath<NormalizedLoad>,
          false
        );
      }
      return;
    }

    ALLOWED_FIELDS.forEach((key) => {
      if (key === 'contact') {
        return;
      }
      if (!(key in selectedLocation)) {
        return;
      }

      const fieldKey = key as keyof TMSLocation;
      const locationValue = selectedLocation[fieldKey];
      const currentValue = formMethods.getValues(
        `${prefix}.${key}` as FieldPath<NormalizedLoad>
      );

      if (key === 'refNumber' && isCreateMode) {
        if (!hasNonEmptyString(currentValue)) {
          applyHydratedValue(
            `${prefix}.${key}` as FieldPath<NormalizedLoad>,
            locationValue
          );
        }
        return;
      }

      if (
        hasNonEmptyString(locationValue) ||
        !hasNonEmptyString(currentValue)
      ) {
        applyHydratedValue(
          `${prefix}.${key}` as FieldPath<NormalizedLoad>,
          locationValue
        );
      }
    });

    const currentApptNote = formMethods.getValues(
      `${prefix}.apptNote` as FieldPath<NormalizedLoad>
    );
    // Transportation Profile defaults: pickup instructions / delivery notes on TMSLocation
    const locationInstructions = instructionsFromTMSLocation(
      selectedLocation,
      stopType
    );
    if (
      hasNonEmptyString(locationInstructions) ||
      !hasNonEmptyString(currentApptNote)
    ) {
      applyHydratedValue(
        `${prefix}.apptNote` as FieldPath<NormalizedLoad>,
        locationInstructions
      );
    }

    const currentHours = formMethods.getValues(hoursPath) || '';
    const locationHours = businessHoursFromTMSLocation(selectedLocation);
    const previousHours = hoursHydrationRef.current;
    const manuallyEdited =
      previousHours?.locationID === normalizedId &&
      previousHours.defaults === defaultValues &&
      currentHours !== previousHours.value;
    if (!manuallyEdited) {
      const hours =
        locationHours || (normalizeBusinessHours(currentHours) ?? currentHours);
      if (hours !== currentHours) {
        applyHydratedValue(hoursPath, hours);
      }
      hoursHydrationRef.current = {
        locationID: normalizedId,
        value: hours,
        defaults: defaultValues,
      };
    }

    // Appointment Required is location-owned (Transportation Profile); always sync.
    applyHydratedValue(
      `${prefix}.apptRequired` as FieldPath<NormalizedLoad>,
      apptRequiredFromTMSLocation(selectedLocation, stopType)
    );
  }, [
    watchedLocationID,
    selectedLocation,
    hoursPath,
    defaultValues,
    applyHydratedValue,
    prefix,
    stopType,
    isCreateMode,
    formMethods,
  ]);

  // Set contact value from selected location (or placeholder if cooler tenant and it's empty)
  // or default placeholder when no location selected.
  useEffect(() => {
    if (!isCoolerTenant) {
      if (
        watchedLocationID &&
        selectedLocation &&
        'contact' in selectedLocation
      ) {
        const locationContact = selectedLocation.contact as MaybeUndef<string>;
        const valueToSet = (locationContact ?? '') as string;
        if (!isCreateMode) {
          applyHydratedValue(
            `${prefix}.contact` as FieldPath<NormalizedLoad>,
            valueToSet
          );
        } else {
          const current = formMethods.getValues(
            `${prefix}.contact` as FieldPath<NormalizedLoad>
          );
          const currentEmpty =
            !current ||
            (typeof current === 'string' && current.trim().length === 0);
          if (currentEmpty) {
            applyHydratedValue(
              `${prefix}.contact` as FieldPath<NormalizedLoad>,
              valueToSet
            );
          }
        }
      }
      return;
    }

    if (watchedLocationID && selectedLocation) {
      const locationContact = selectedLocation.contact as MaybeUndef<string>;
      const contactEmpty =
        locationContact == null ||
        (typeof locationContact === 'string' &&
          locationContact.trim().length === 0);
      const valueToSet = contactEmpty
        ? DEFAULT_CONTACT_PLACEHOLDER
        : (locationContact ?? '');
      const currentValue = formMethods.getValues(
        `${prefix}.contact` as FieldPath<NormalizedLoad>
      );
      const currentEmpty =
        !currentValue ||
        (typeof currentValue === 'string' &&
          (currentValue.trim().length === 0 ||
            currentValue === DEFAULT_CONTACT_PLACEHOLDER));
      if (currentEmpty) {
        applyHydratedValue(
          `${prefix}.contact` as FieldPath<NormalizedLoad>,
          valueToSet as string
        );
      }
    } else {
      const currentContact = formMethods.getValues(
        `${prefix}.contact` as FieldPath<NormalizedLoad>
      );
      if (
        !currentContact ||
        (typeof currentContact === 'string' &&
          currentContact.trim().length === 0)
      ) {
        applyHydratedValue(
          `${prefix}.contact` as FieldPath<NormalizedLoad>,
          DEFAULT_CONTACT_PLACEHOLDER
        );
      }
    }
  }, [
    watchedLocationID,
    selectedLocation,
    applyHydratedValue,
    prefix,
    isCreateMode,
    isCoolerTenant,
    formMethods,
  ]);

  // Clear externalTMSID when user manually edits address fields away from the
  // selected location. Skip the check if the location ID just changed—gives the
  // hydrate effect time to populate form fields before we compare them.
  useEffect(() => {
    const normalizedId = normalizeExternalTMSID(watchedLocationID);
    const prevId = prevLocationIDRef.current;

    // Update the ref for next render
    prevLocationIDRef.current = normalizedId || null;

    if (!normalizedId || !selectedLocation) {
      return;
    }

    // Skip the mismatch check if the location ID just changed this render cycle.
    // The form fields still hold OLD values; the hydrate effect will populate
    // them with the new location's data. Clearing now would race the hydrate.
    if (prevId !== normalizedId) {
      return;
    }

    const fieldsToCheck: Array<
      'name' | 'addressLine1' | 'addressLine2' | 'city' | 'state' | 'zipCode'
    > = ['name', 'addressLine1', 'addressLine2', 'city', 'state', 'zipCode'];

    const hasFieldDifferences = fieldsToCheck.some((field) => {
      const selectedValue = selectedLocation[field];
      const watchedValue = watchedLocObj?.[field];
      return (
        hasNonEmptyString(selectedValue) &&
        hasNonEmptyString(watchedValue) &&
        watchedValue?.toLowerCase() !== selectedValue?.toLowerCase()
      );
    });

    if (hasFieldDifferences) {
      setValue(`${prefix}.externalTMSID`, '', { shouldDirty: true });
    }
  }, [
    watchedLocationID,
    selectedLocation,
    watchedLocObj,
    watchedLocObj?.name,
    watchedLocObj?.addressLine1,
    watchedLocObj?.addressLine2,
    watchedLocObj?.city,
    watchedLocObj?.state,
    watchedLocObj?.zipCode,
    setValue,
    prefix,
  ]);

  // Handle mode switching - store/restore location field values only when
  // entryMode actually changes (not when locationFieldValues updates mid-mode).
  useEffect(() => {
    const prevEntryMode = prevEntryModeRef.current;
    prevEntryModeRef.current = entryMode;
    if (prevDefaultValuesRef.current !== defaultValues) {
      prevDefaultValuesRef.current = defaultValues;
      // A form reset starts new drafts. Its values already belong to the new
      // mode, so never restore fields cached before the reset over them.
      quickEntryNameRef.current = undefined;
      setLocationFieldValues({});
      return;
    }
    if (prevEntryMode === entryMode) {
      return;
    }

    if (entryMode === StopEntryMode.Quick) {
      const currentName = formMethods.getValues(
        `${prefix}.name` as FieldPath<NormalizedLoad>
      );
      const currentAddressLine1 = formMethods.getValues(
        `${prefix}.addressLine1` as FieldPath<NormalizedLoad>
      );
      const currentAddressLine2 = formMethods.getValues(
        `${prefix}.addressLine2` as FieldPath<NormalizedLoad>
      );
      const currentExternalTMSID = formMethods.getValues(
        `${prefix}.externalTMSID` as FieldPath<NormalizedLoad>
      );
      const currentTimezone = formMethods.getValues(
        `${prefix}.timezone` as FieldPath<NormalizedLoad>
      );

      setLocationFieldValues({
        name: typeof currentName === 'string' ? currentName : '',
        addressLine1:
          typeof currentAddressLine1 === 'string' ? currentAddressLine1 : '',
        addressLine2:
          typeof currentAddressLine2 === 'string' ? currentAddressLine2 : '',
        externalTMSID:
          typeof currentExternalTMSID === 'string' ? currentExternalTMSID : '',
        timezone: typeof currentTimezone === 'string' ? currentTimezone : '',
      });

      setValue(
        `${prefix}.name` as FieldPath<NormalizedLoad>,
        quickEntryNameRef.current ?? currentName ?? '',
        { shouldDirty: false }
      );
      setValue(`${prefix}.addressLine1` as FieldPath<NormalizedLoad>, '', {
        shouldDirty: false,
      });
      setValue(`${prefix}.addressLine2` as FieldPath<NormalizedLoad>, '', {
        shouldDirty: false,
      });
      setValue(`${prefix}.externalTMSID` as FieldPath<NormalizedLoad>, '', {
        shouldDirty: false,
      });
    } else if (entryMode === StopEntryMode.Location) {
      quickEntryNameRef.current = formMethods.getValues(`${prefix}.name`) || '';
      if (locationFieldValues.name !== undefined) {
        setValue(
          `${prefix}.name` as FieldPath<NormalizedLoad>,
          locationFieldValues.name,
          {
            shouldDirty: false,
          }
        );
      }
      if (locationFieldValues.addressLine1) {
        setValue(
          `${prefix}.addressLine1` as FieldPath<NormalizedLoad>,
          locationFieldValues.addressLine1,
          {
            shouldDirty: false,
          }
        );
      }
      if (locationFieldValues.addressLine2) {
        setValue(
          `${prefix}.addressLine2` as FieldPath<NormalizedLoad>,
          locationFieldValues.addressLine2,
          {
            shouldDirty: false,
          }
        );
      }
      if (locationFieldValues.externalTMSID) {
        setValue(
          `${prefix}.externalTMSID` as FieldPath<NormalizedLoad>,
          locationFieldValues.externalTMSID,
          {
            shouldDirty: false,
          }
        );
      }
      if (locationFieldValues.timezone !== undefined) {
        setValue(
          `${prefix}.timezone` as FieldPath<NormalizedLoad>,
          locationFieldValues.timezone,
          {
            shouldDirty: false,
          }
        );
      }
    }
  }, [
    entryMode,
    defaultValues,
    prefix,
    setValue,
    formMethods,
    locationFieldValues,
  ]);

  return (
    <>
      <AILocationHintBanner isVisible={!!shouldShowAIHint} />

      {/* Entry Mode Toggle */}
      <Flex direction='col' gap='xs' className='mb-3'>
        <ToggleGroup
          type='single'
          value={entryMode}
          onValueChange={(value: string) => {
            if (
              value &&
              (value === StopEntryMode.Quick ||
                value === StopEntryMode.Location)
            ) {
              setEntryMode(value as StopEntryMode);
            }
          }}
          className='relative bg-neutral-100 border border-neutral-300 rounded p-0.5 grid w-full grid-cols-2 gap-0 m-0 text-sm'
        >
          {/* Sliding background */}
          <div
            className={cn(
              'absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded bg-brand-main transition-all duration-200 ease-in-out',
              {
                'left-0.5': entryMode === StopEntryMode.Location,
                'left-[calc(50%+0.5px)]': entryMode === StopEntryMode.Quick,
              }
            )}
          />

          <ToggleGroupItem
            value={StopEntryMode.Location}
            aria-label='Location Entry'
            className='relative z-10 flex-1 bg-transparent border-0 shadow-none data-[state=on]:text-white data-[state=off]:text-neutral-600 text-sm py-1 px-2'
          >
            Location Entry
          </ToggleGroupItem>
          <ToggleGroupItem
            value={StopEntryMode.Quick}
            aria-label='Quick Entry'
            className='relative z-10 flex-1 bg-transparent border-0 shadow-none data-[state=on]:text-white data-[state=off]:text-neutral-600 text-sm py-1 px-2'
          >
            Quick Entry
          </ToggleGroupItem>
        </ToggleGroup>
      </Flex>

      {entryMode === StopEntryMode.Location && (
        <>
          <RHFLocationSelect
            name={`${prefix}.externalTMSID`}
            label='Location'
            control={control}
            errors={formMethods.formState.errors}
            locations={locations}
            setLocations={setLocations}
            isLoadingLocations={isLoadingLocations}
            handleRefreshLocations={handleRefreshLocations}
            required={!hasValidAISuggestion}
            placeholder='Search by name or address'
          />

          <RHFTextInput
            name={`${prefix}.name`}
            label='Name'
            readOnly={locationFieldsReadOnly}
            required={false}
          />

          <RHFTextInput
            name={`${prefix}.addressLine1`}
            label='Address Line 1'
            readOnly={locationFieldsReadOnly}
          />

          <RHFTextInput
            name={`${prefix}.addressLine2`}
            label='Address Line 2'
            readOnly={locationFieldsReadOnly}
          />
        </>
      )}
      {entryMode === StopEntryMode.Quick && (
        <RHFTextInput
          name={`${prefix}.name`}
          label='Facility Name'
          readOnly={false}
          required={false}
        />
      )}
      {/* City and State on same row - City 3/4, State 1/4 */}
      <Grid cols='4' gap='sm' width='full' className='mx-0'>
        <div className='col-span-3'>
          <RHFTextInput
            name={`${prefix}.city`}
            label='City'
            readOnly={locationFieldsReadOnly}
            required={true}
          />
        </div>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${prefix}.state`}
            label='State'
            readOnly={locationFieldsReadOnly}
            required={entryMode === StopEntryMode.Quick}
          />
        </div>
      </Grid>
      {/* Zip Code and Country on same row */}
      <Grid cols='2' gap='sm' width='full' className='mx-0'>
        <RHFTextInput
          name={`${prefix}.zipCode`}
          label='Zip Code'
          readOnly={locationFieldsReadOnly}
          required={true}
        />
        <RHFTextInput
          name={`${prefix}.country`}
          label='Country'
          readOnly={locationFieldsReadOnly}
          required={true}
        />
      </Grid>
      <RHFTextInput
        name={`${prefix}.contact`}
        label='Contact Name'
        placeholder={isCoolerTenant ? 'First Last' : undefined}
        required={isCreateMode && isCoolerTenant}
        readOnly={!isCreateMode}
        options={
          isCreateMode && isCoolerTenant
            ? {
                required: 'Contact name is required',
                validate: (value: string) => {
                  if (!value || value.trim().length === 0) {
                    return 'Contact name is required';
                  }
                  const parts = value
                    .trim()
                    .split(/\s+/)
                    .filter((p) => p.length > 0);
                  if (parts.length < 2) {
                    return 'Please enter both first and last name (e.g., "John Smith")';
                  }
                  return true;
                },
              }
            : undefined
        }
      />
      <RHFTextInput name={`${prefix}.phone`} label='Phone' readOnly={false} />
      <RHFTextInput name={`${prefix}.email`} label='Email' readOnly={false} />
      <RHFTextInput
        name={`${prefix}.businessHours`}
        label='Shipping/Receiving Hours'
        placeholder='09:00-17:00'
        readOnly={false}
        options={{
          onBlur: () => {
            const value = getValues(hoursPath) || '';
            const normalized = normalizeBusinessHours(value);
            setValue(hoursPath, normalized ?? value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          },
          validate: (value: string) =>
            normalizeBusinessHours(value || '') !== null ||
            'Use 24-hour HH:MM-HH:MM (e.g. 06:00-15:00), explicit AM/PM times, or leave hours empty.',
        }}
      />
      {!refNumberAttr?.isNotSupported && (
        <LoadRefPillInput
          name={`${prefix}.refNumber`}
          control={control}
          label='Reference Number'
          disabled={Boolean(!isCreateMode || refNumberAttr?.isReadOnly)}
          originalSuggestionData={
            originalSuggestionData || formMethods.getValues()
          }
        />
      )}

      {stopType === 'pickup' && (
        <LoadDateInput
          name={`${prefix}.readyTime` as FieldPath<NormalizedLoad>}
          label='Pickup Date'
          required={true}
          utcWallClock
          displayTimezone={getDefaultTimezone()}
        />
      )}
      {stopType === 'consignee' && (
        <LoadDateInput
          name={`${prefix}.mustDeliver` as FieldPath<NormalizedLoad>}
          label='Delivery Date'
          required={true}
          utcWallClock
          displayTimezone={getDefaultTimezone()}
        />
      )}

      <Controller
        name={`${prefix}.apptRequired` as FieldPath<NormalizedLoad>}
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={field.value === true}
            disabled
            label='Appointment Required'
            labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
          />
        )}
      />

      <LoadDateTimeInput
        name={`${prefix}.apptStartTime`}
        label='Appointment Start Time'
        options={datetimeFieldOptions}
        defaultTimezone={getDefaultTimezone()}
      />

      <LoadDateTimeInput
        name={`${prefix}.apptEndTime`}
        label='Appointment End Time'
        options={datetimeFieldOptions}
        defaultTimezone={getDefaultTimezone()}
      />

      <div className='w-full'>
        <Label name={`${prefix}.apptNote`} className='text-neutral-500 mb-1'>
          Instructions
        </Label>
        <Controller
          name={`${prefix}.apptNote` as FieldPath<NormalizedLoad>}
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              value={typeof field.value === 'string' ? field.value : ''}
              className='mt-1 !min-h-[2.5rem] resize-y whitespace-pre-wrap leading-5'
              rows={1}
            />
          )}
        />
      </div>
    </>
  );
}

export default StopForm;

export function hasValidAISuggestedLocation(
  stop: LoadStopTypes,
  formValues: NormalizedLoad,
  originalSuggestionData?: NormalizedLoad
): boolean {
  const stopData = originalSuggestionData?.[stop];
  const formStopData = formValues[stop];

  if (!stopData) {
    return false;
  }

  if (
    normalizeExternalTMSID(stopData.externalTMSID) ||
    normalizeExternalTMSID(formStopData?.externalTMSID)
  ) {
    return false;
  }

  const hasRequiredFields =
    formStopData?.name &&
    formStopData?.addressLine1 &&
    formStopData?.city &&
    formStopData?.state &&
    formStopData?.zipCode;

  return !!hasRequiredFields;
}
